import React, {FC} from 'react';
import {SxProps} from '@mui/system/styleFunctionSx/styleFunctionSx';
import Box from '@mui/material/Box';
import BarChart from '@/components/Charts/BarChart';
import {DashboardData, UuidOptionColor} from '@/domain/services/Clas';
import GrassIcon from '@mui/icons-material/Grass';
import {useTranslation} from 'react-i18next';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

const containerSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
  m: 2
};

const horizontalContainer: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 2
};

const socContainer: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: 5
};

export interface DashboardProps {
  data: DashboardData | undefined,
  crops?: Array<UuidOptionColor>
  uses?: Array<UuidOptionColor>
}

const DashboardView: FC<DashboardProps> = ({data, crops, uses}) => {
  const {t} = useTranslation();

  const protectionData = [
    {
      uuid: 'rnProtectedAreaTotalInHa',
      totalAreaInHa: data?.rnProtectedAreaTotalInHa || 0
    },
    {
      uuid: 'enpProtectedAreaTotalInHa',
      totalAreaInHa: data?.enpProtectedAreaTotalInHa || 0
    },
    {
      uuid: 'mabProtectedAreaTotalInHa',
      totalAreaInHa: data?.mabProtectedAreaTotalInHa || 0
    },
    {
      uuid: 'protectionTypeNoAreaTotalInHa',
      totalAreaInHa: data?.protectionTypeNoAreaTotalInHa || 0
    }
  ];

  const protectionLabels = [
    {
      uuid: 'enpProtectedAreaTotalInHa',
      name: t('dashboard.enpProtectedAreaTotalInHa'),
      color: '#eada52'
    },
    {
      uuid: 'mabProtectedAreaTotalInHa',
      name: t('dashboard.mabProtectedAreaTotalInHa'),
      color: '#37af7b'
    },{
      uuid: 'rnProtectedAreaTotalInHa',
      name: t('dashboard.rnProtectedAreaTotalInHa'),
      color: '#dd6268'
    },
    {
      uuid: 'protectionTypeNoAreaTotalInHa',
      name: t('dashboard.protectionTypeNoAreaTotalInHa'),
      color: '#6c6c6c'
    }
  ];

  const dataWithProtection = {
    ...(data as DashboardData),
    use: data?.use ?? [],
    crop: data?.crop ?? [],
    protection: protectionData
  };

  const CHART_WIDTH = 500;

  return <Box sx={containerSx}>
    {
      data && <>
        {
          uses && <>
            <BarChart data={dataWithProtection} labels={uses} width={CHART_WIDTH} legendColumns={dataWithProtection['use'].length < 10 ? 1 : 2} title={t('dashboard.areaByUse')} dataType='use'/>
            <Divider/>
          </>
        }
        {
          crops && <>
            <BarChart data={dataWithProtection} labels={crops} width={CHART_WIDTH} legendColumns={dataWithProtection['crop'].length < 10 ? 1 : 2} title={t('dashboard.areaByCrop')} dataType='crop'/>
            <Divider/>
          </>
        }
        <Box sx={{...horizontalContainer, alignItems: 'flex-start', justifyContent: 'flex-start'}}>
          <BarChart data={dataWithProtection} labels={protectionLabels} width={CHART_WIDTH} legendColumns={1} title={t('dashboard.areaByProtection')} dataType='protection'/>
          <Box sx={socContainer}>
            <Typography color='primary' sx={{fontWeight: 900, mt: 2}}>{t('infoPanel.soilOrganicCarbon_def').toUpperCase()}</Typography>
            <Box sx={{...horizontalContainer}}>
              <GrassIcon color='primary' sx={{fontSize: '125px', color: 'primary'}}/>
              <Typography variant='h3' sx={{color: '#4c4c4c'}}>
                {
                  !data.soilOrganicCarbonTotal
                    ? 'No hay datos'
                    : Number(data.soilOrganicCarbonTotal?.toFixed()).toLocaleString('es-ES')
                }
              </Typography>
            </Box>

          </Box>
          <Divider/>
        </Box>
      </>
    }
  </Box>;
};

export default DashboardView;